import { skipToken } from "@reduxjs/toolkit/query";
import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/configurationAgent";
import {
  copilotProposalIdSchema,
  type CopilotProposal,
  type CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import type { SignalId, SignalPayload } from "@repo/app-types/entities/signal";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DateTime } from "luxon";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  useGetPendingSignalProposalQuery,
  useResolveProposalMutation,
} from "../../../api/entities/copilotProposal.js";
import {
  useCreateSignalMutation,
  useDeleteSignalMutation,
  useGetSignalQuery,
  useSaveSignalMutation,
  useTestSignalProposalMutation,
} from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard.js";
import { SignalEditorContainer } from "./signalEditorContainer.js";
import { SIGNAL_PROPOSAL_QUERY_PARAM } from "./signalEditorUrlState.js";
import {
  cleanMetadataSchema,
  isSignalFormDirty,
  mergeProposalIntoForm,
  readFormFields,
  type SignalFormFields,
} from "./signalFormHelpers.js";
import {
  SignalTestConfigModal,
  type TestSignalFiltersUi,
} from "./signalTestConfigModal.js";
import { SignalTestMatchesModal } from "./signalTestMatchesModal.js";

type TestResultsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

const EMPTY_FORM: SignalFormFields = {
  name: "",
  description: "",
  metadata_schema: { fields: [] },
  playbook_scope: { type: "all" },
};

/**
 * Signals use explicit saves: edits stay local until Save makes them the
 * signal's new live version.
 */
export const SignalEditorPage: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { signalId } = useParams<{ signalId: SignalId }>();
  const isNew = !signalId;
  const navigate = useNavigate();

  const [createdSignalId, setCreatedSignalId] = useState<SignalId | null>(null);
  const createdSignalIdRef = useRef<SignalId | null>(null);

  const {
    data: signal,
    isLoading: isLoadingSignal,
    isError,
  } = useGetSignalQuery(signalId ?? createdSignalId ?? skipToken);
  const [createSignal, { isLoading: isCreating }] = useCreateSignalMutation();
  const [saveSignal, { isLoading: isSavingSignal }] = useSaveSignalMutation();
  const [deleteSignal, { isLoading: isDeleting }] = useDeleteSignalMutation();
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const [testSignalProposal] = useTestSignalProposalMutation();

  // The draft the user edits. A proposal is laid over it for display only
  // ({@link displayedForm}), so discarding one needs no restore and applying
  // one is a merge.
  const [form, setForm] = useState<SignalFormFields>(EMPTY_FORM);
  const [proposal, setProposal] = useState<CopilotProposal<"signal"> | null>(
    null,
  );
  // What the form shows: the draft, with a pending proposal laid over it. The
  // draft itself is never written to until the proposal is applied.
  const displayedForm = useMemo(
    () => (proposal ? mergeProposalIntoForm(form, proposal.data) : form),
    [form, proposal],
  );
  // The `create_signal` proposal the draft was applied from, if any. The new
  // signal takes the id it minted, the one the chat already knows it by.
  const createdFromProposalIdRef = useRef<CopilotProposalId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // The form is seeded from the stored draft once; later cache updates are
  // the form's own saves.
  const [isFormInitialized, setIsFormInitialized] = useState(isNew);

  const [testResultsState, setTestResultsState] = useState<TestResultsState>({
    status: "idle",
  });
  // Within-session memory of the filters used on the last actual run. When
  // non-null, the main Test signal click re-runs with these directly; otherwise
  // it opens the config modal so the user can pick.
  const [lastRunFilters, setLastRunFilters] =
    useState<TestSignalFiltersUi | null>(null);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);

  const updateForm = useCallback((fields: Partial<SignalFormFields>) => {
    setForm((current) => ({ ...current, ...fields }));
  }, []);

  useEffect(() => {
    if (signal && !isFormInitialized) {
      setForm(readFormFields(signal));
      setIsFormInitialized(true);
    }
  }, [signal, isFormInitialized]);

  // Until a test is explicitly configured, tests follow the form's current
  // playbook scope. Memoized because the config modal re-seeds its local state
  // whenever this object's identity changes.
  const effectiveTestFilters: TestSignalFiltersUi = useMemo(
    () =>
      lastRunFilters ?? {
        playbookScope: form.playbook_scope,
        teammateGroupIds: null,
        dateRange: defaultDateRange,
      },
    [lastRunFilters, form.playbook_scope],
  );

  // The form as the server would store it. Dirty state is compared with the
  // live version because the user has not committed an edit until Save.
  const draftPayload = useMemo<SignalPayload>(
    () => ({
      name: form.name.trim(),
      description: form.description.trim(),
      metadata_schema: cleanMetadataSchema(form.metadata_schema),
      playbook_scope: form.playbook_scope,
    }),
    [form],
  );
  const isFormValid =
    draftPayload.name !== "" && draftPayload.description !== "";
  // Test signal runs what the form shows, a suggestion under review included.
  const isDisplayedFormTestable =
    displayedForm.name.trim() !== "" && displayedForm.description.trim() !== "";
  // A signal that has never been saved live has nothing to be unchanged from.
  const savedSignal = signal?.live_version_id ? signal : undefined;
  const hasUnsavedChanges = isSignalFormDirty(
    savedSignal,
    draftPayload.name,
    draftPayload.description,
    draftPayload.metadata_schema ?? { fields: [] },
    draftPayload.playbook_scope,
  );

  const runTestWithFilters = useCallback(
    async (filters: TestSignalFiltersUi) => {
      if (!isDisplayedFormTestable) return;

      setTestResultsState({ status: "loading" });

      const now = DateTime.now();
      const rangeEnd = filters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(filters.dateRange.length);

      try {
        const data = await testSignalProposal({
          ...(signalId ? { signalId } : {}),
          signal: {
            name: displayedForm.name,
            description: displayedForm.description,
            metadata_schema: cleanMetadataSchema(displayedForm.metadata_schema),
            playbook_scope: displayedForm.playbook_scope,
          },
          filters: {
            playbookScope: filters.playbookScope,
            ...(filters.teammateGroupIds
              ? { teammateGroupIds: filters.teammateGroupIds }
              : {}),
            lastRecordedAtRange: {
              after: rangeStart.startOf("day").toUTC().toISO(),
              before: rangeEnd.endOf("day").toUTC().toISO(),
            },
          },
        }).unwrap();

        setTestResultsState({
          status: "complete",
          data,
          completedAt: DateTime.now(),
        });
      } catch (err) {
        console.error("Failed to test signal:", err);
        setTestResultsState({ status: "error" });
      }
    },
    [signalId, displayedForm, isDisplayedFormTestable, testSignalProposal],
  );

  const handleRunTestFromModal = useCallback(
    (filters: TestSignalFiltersUi) => {
      setIsTestConfigOpen(false);
      setLastRunFilters(filters);
      runTestWithFilters(filters);
    },
    [runTestWithFilters],
  );

  // Open config modal if filters have not been configured yet
  const handleTestSignalClick = useCallback(() => {
    if (lastRunFilters) {
      runTestWithFilters(lastRunFilters);
    } else {
      setIsTestConfigOpen(true);
    }
  }, [lastRunFilters, runTestWithFilters]);

  const handleConfigureTestClick = useCallback(() => {
    setIsTestConfigOpen(true);
  }, []);

  // One-shot deep link from an Ask Alpharun proposal card (?proposalId=...):
  // the proposal's part is laid over the form as a read-only preview and its
  // evidence becomes the test result. Apply resolves the proposal and leaves
  // the result as an unsaved form edit; Discard restores the stored version. A
  // proposal for another signal loads nothing. While the link is unresolved,
  // the form is held and no proposal actions are shown.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(SIGNAL_PROPOSAL_QUERY_PARAM),
  ).data;
  const {
    currentData: deepLinkProposal,
    isError: isDeepLinkProposalUnavailable,
  } = useGetPendingSignalProposalQuery(deepLinkProposalId ?? skipToken);
  const isLoadingProposal = deepLinkProposalId !== undefined;
  const clearDeepLinkProposalId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SIGNAL_PROPOSAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  useEffect(() => {
    if (deepLinkProposalId && isDeepLinkProposalUnavailable) {
      clearDeepLinkProposalId();
      setError("This suggestion is no longer available.");
    }
  }, [
    deepLinkProposalId,
    isDeepLinkProposalUnavailable,
    clearDeepLinkProposalId,
  ]);
  useEffect(() => {
    if (
      !isFormInitialized ||
      !deepLinkProposalId ||
      !deepLinkProposal ||
      deepLinkProposal.id !== deepLinkProposalId
    ) {
      return;
    }
    clearDeepLinkProposalId();
    const { data } = deepLinkProposal;
    const targetsThisPage =
      data.kind === "create_signal"
        ? isNew
        : deepLinkProposal.resource_id === signalId;
    if (!targetsThisPage) {
      return;
    }
    setTestResultsState({ status: "idle" });
    setError(null);
    if ("evidence" in data) {
      setTestResultsState({
        status: "complete",
        data: data.evidence,
        completedAt: DateTime.fromISO(deepLinkProposal.created_at),
      });
    }
    setProposal(deepLinkProposal);
  }, [
    isFormInitialized,
    deepLinkProposalId,
    deepLinkProposal,
    isNew,
    signalId,
    clearDeepLinkProposalId,
  ]);

  const isSaving = isCreating || isSavingSignal;
  const isBusy = isLoadingProposal || isResolvingProposal || isSaving;

  const { guardModal, navigateWithoutConfirmation } = useUnsavedChangesGuard(
    hasUnsavedChanges && proposal === null,
  );

  if (!isNew && (isLoadingSignal || !isFormInitialized)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} isLoading />
      </div>
    );
  }

  if (!isNew && (isError || !signal)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} title="Signal not found" />
      </div>
    );
  }

  /** Drop the previewed proposal. The draft under it was never touched. */
  const dropProposal = () => {
    setTestResultsState({ status: "idle" });
    setProposal(null);
  };

  const handleSave = async () => {
    if (!isFormValid || isBusy || proposal || !hasUnsavedChanges) return;
    setError(null);
    try {
      const targetSignalId = signalId ?? createdSignalIdRef.current;
      if (targetSignalId) {
        const saved = await saveSignal({
          id: targetSignalId,
          ...draftPayload,
        }).unwrap();
        setForm(readFormFields(saved));
        return;
      }
      const created = await createSignal({
        ...draftPayload,
        ...(createdFromProposalIdRef.current
          ? { proposal_id: createdFromProposalIdRef.current }
          : {}),
      }).unwrap();
      createdSignalIdRef.current = created.id;
      setCreatedSignalId(created.id);
      setForm(readFormFields(created));
      navigateWithoutConfirmation(() => {
        void navigate(appPaths.settingsSignal.build({ signalId: created.id }), {
          replace: true,
        });
      });
    } catch (err: any) {
      console.error("Failed to save signal:", err);
      setError(err.data?.message || "Failed to save signal. Please try again.");
    }
  };

  /** Drop the unsaved edits: back to the saved signal, or out of a new one. */
  const handleCancel = () => {
    if (isBusy) {
      return;
    }
    setError(null);
    if (!signal) {
      navigateWithoutConfirmation(onClose);
      return;
    }
    setForm(readFormFields(signal));
  };

  // Applying a suggestion resolves it and puts it into the draft. Save then
  // writes the draft like any other edit.
  const handleApplyProposal = async () => {
    if (!proposal || isBusy) return;
    setError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "applied",
      }).unwrap();
    } catch (err: any) {
      if (err.status !== 409) {
        console.error("Failed to apply the suggestion:", err);
        setError("Failed to apply the suggestion. Please try again.");
        return;
      }
      // A proposal that has moved on can no longer be applied.
      setError("This suggestion is no longer available.");
      dropProposal();
      return;
    }
    if (proposal.data.kind === "create_signal") {
      createdFromProposalIdRef.current = proposal.id;
    }
    setForm(displayedForm);
    setProposal(null);
  };

  const handleDelete = async () => {
    const targetSignalId = signalId ?? createdSignalIdRef.current;
    if (!targetSignalId || isDeleting) {
      return;
    }
    try {
      await deleteSignal(targetSignalId).unwrap();
      navigateWithoutConfirmation(onClose);
    } catch (err: any) {
      console.error("Failed to delete signal:", err);
      setError(
        err.data?.message || "Failed to delete signal. Please try again.",
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleDiscardProposal = async () => {
    if (!proposal || isBusy) return;
    setError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "discarded",
      }).unwrap();
    } catch (err: any) {
      // A proposal that has moved on is already not being applied, so the
      // preview is dropped either way.
      if (err.status !== 409) {
        console.error("Failed to discard the suggestion:", err);
        setError("Failed to discard the suggestion. Please try again.");
        return;
      }
      setError("This suggestion is no longer available.");
    }
    dropProposal();
  };

  const testResultPillState =
    testResultsState.status === "idle" ? null : testResultsState;

  return (
    <>
      {guardModal}
      <SimpleModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        variant={SimpleModalVariant.Warning}
        title="Delete signal"
        subtitle="Are you sure you want to delete this signal? This action can't be undone."
        confirmButtonText="Delete signal"
        onConfirm={() => {
          void handleDelete();
        }}
      />
      <div className="flex h-full flex-col">
        <SignalEditorContainer
          name={displayedForm.name}
          description={displayedForm.description}
          metadataSchema={displayedForm.metadata_schema}
          playbookScope={displayedForm.playbook_scope}
          error={error}
          onNameChange={(name) => {
            updateForm({ name });
          }}
          onDescriptionChange={(description) => {
            updateForm({ description });
          }}
          onMetadataSchemaChange={(metadataSchema) => {
            updateForm({ metadata_schema: metadataSchema });
          }}
          onPlaybookScopeChange={(playbookScope) => {
            updateForm({ playbook_scope: playbookScope });
          }}
          // Two baselines, as the playbook editor's panes do: a proposal reads
          // against the draft it is laid over, and an edit of your own against
          // what is live today.
          diffBaseline={
            proposal
              ? form
              : savedSignal
                ? readFormFields(savedSignal)
                : undefined
          }
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          onDelete={
            signalId
              ? () => {
                  setIsDeleteModalOpen(true);
                }
              : undefined
          }
          isSaving={isSaving}
          isDeleting={isDeleting}
          isSaveDisabled={isBusy || !isFormValid || !hasUnsavedChanges}
          hasUnsavedChanges={hasUnsavedChanges}
          onTestSignalClick={handleTestSignalClick}
          onConfigureTestClick={handleConfigureTestClick}
          isTestSignalDisabled={
            !isDisplayedFormTestable || testResultsState.status === "loading"
          }
          testResultPillState={testResultPillState}
          onViewMatches={() => setIsMatchesModalOpen(true)}
          suggestion={
            proposal && !isLoadingProposal
              ? {
                  isNewSignal: isNew,
                  onDiscard: handleDiscardProposal,
                  onApply: handleApplyProposal,
                  isResolving: isResolvingProposal,
                }
              : undefined
          }
        />
      </div>

      <SignalTestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        initialFilters={effectiveTestFilters}
        onRunTest={handleRunTestFromModal}
      />

      {testResultsState.status === "complete" ? (
        <SignalTestMatchesModal
          isOpen={isMatchesModalOpen}
          onClose={() => setIsMatchesModalOpen(false)}
          data={testResultsState.data}
          metadataSchema={displayedForm.metadata_schema}
        />
      ) : null}
    </>
  );
};
